import React from "react";
import { Box, Typography } from "@mui/material";
import { toneColors, useUtilisationColors } from "./useUtilisationColors";

// Small building blocks used by the popup cards. Kept in one file because each is
// a handful of lines and they are always used together.

// Upper-case section label ("YOUR CONTRIBUTION", "PROJECT HEALTH").
export const SectionLabel = ({ children, ...rest }) => {
  const colors = useUtilisationColors();
  return (
    <Typography
      {...rest}
      sx={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: colors.ink2,
      }}
    >
      {children}
    </Typography>
  );
};

// A figure with a caption: "Your utilisation 30% of project budget".
export const UtilisationStat = ({ label, value, caption, tone, compact = false, outlined = true }) => {
  const colors = useUtilisationColors();
  const valueColor = tone ? toneColors(colors, tone).main : colors.ink;

  return (
    <Box
      sx={{
        p: compact ? "8px 10px" : "12px",
        border: outlined ? `1px solid ${colors.line}` : "none",
        borderRadius: compact ? "8px" : "12px",
        background: outlined ? "transparent" : colors.head,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: compact ? 11 : 12, color: colors.ink2 }}>{label}</Typography>
      <Typography sx={{ fontSize: compact ? 18 : 22, fontWeight: 800, color: valueColor, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {caption && <Typography sx={{ fontSize: 12, color: colors.ink2 }}>{caption}</Typography>}
    </Box>
  );
};

// Pill such as "Within budget" / "Budget exceeded".
export const StatusChip = ({ tone = "ok", children }) => {
  const colors = useUtilisationColors();
  const { main, wash } = toneColors(colors, tone);
  return (
    <Box
      component="span"
      sx={{ px: "10px", py: "3px", borderRadius: "999px", fontSize: 12, fontWeight: 700, background: wash, color: main }}
    >
      {children}
    </Box>
  );
};

// Full-width message strip (incentive eligibility, over-budget warning).
export const Notice = ({ tone = "ok", children, compact = false, ...rest }) => {
  const colors = useUtilisationColors();
  const { main, wash } = toneColors(colors, tone);
  return (
    <Box
      {...rest}
      sx={{
        p: compact ? "7px 10px" : "9px 12px",
        borderRadius: compact ? "8px" : "10px",
        background: wash,
        color: main,
        fontSize: compact ? 12 : 13,
        fontWeight: 700,
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  );
};
